import React from "react";
import { Table, Tag, Space, Button, Dropdown, Grid } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useEffect } from "react";
import {
  EyeOutlined,
  MessageOutlined,
  PhoneOutlined,
  UserOutlined,
  MoreOutlined,
  WhatsAppOutlined,
  EditOutlined,
  CalendarOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import TaskDrawer from "../../TodayTasks/components/TaskDrawer";
import { useDispatch, useSelector } from "react-redux";
import { fetchCrmFieldsApi } from "../../../../apiservice/crm";
import { fetchLeads } from "../../Leads/leadThunk";
import { convertToCompanyTimezoneObject } from "../../../../lib/functions";
import { resolveDialableNumber, resolveWhatsAppNumber } from "../../../../lib/phone";

const DealTable = ({ deals, onViewActivity  }) => {
  const [customFields, setCustomFields] = useState([]);
  const [taskDrawer, setTaskDrawer] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);

  const {list : leads} = useSelector(s => s.leads)

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const screens = Grid.useBreakpoint();
  // A pinned Actions column needs spare width to pin against; on a phone it
  // just floats over the first columns, so it moves under the row instead.
  const isMobile = !screens.md;

  useEffect(() => {
      const loadFields = async () => {
        try {
          const res = await fetchCrmFieldsApi({module :"deal"});          
          setCustomFields(res?.data || []);
        } catch (err) {
          console.log(err);
        }
      };
      loadFields();
      dispatch(fetchLeads());
    }, [dispatch]);


  const baseColumns = [
    {
      title: "No",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Deal ID",
      dataIndex: "deal_id",
      render: (v) => <span style={{ color: "#1677ff" }}>{v?.slice(0, 8)}</span>,
    },
    {
      title: "Deal Name",
      dataIndex: "title",
    },
    {
      title: "Lead",
      dataIndex: "contact_name",
    },
    {
      title: "Amount",
      dataIndex: "value",
      render: (v) => `₹ ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Stage",
      dataIndex: "stage",
      render: (stage) => {
        const colorMap = {
          prospecting: "orange",
          negotiation: "blue",
          won: "green",
          lost: "red",
        };

        return (
          <Tag color={colorMap[stage] || "default"}>
            {stage?.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Close Date",
      dataIndex: "expected_close_date",
      render: (d) => (d ? dayjs(d).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Last Activity",
      dataIndex: "last_activity_at",
      render: (d) =>
        d ? convertToCompanyTimezoneObject(d).format("DD MMM, hh:mm A") : "No activity",
    },
    {
      title: "Activities",
      dataIndex: "activity_count",
      render: (v) => v || 0,
    },
  ];

  const renderActions = (record) => {
      const menuItems = [
        {
          key: "task",
          icon: <CalendarOutlined />,
          label: "Add Task",

          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();

            setSelectedDeal(record);
            setTaskDrawer(true);
          },
        },

        {
          key: "call",
          icon: <PhoneOutlined />,
          label: "Call",

          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            window.location.href = `tel:${resolveDialableNumber(record.phone, record.country)}`;
          },
        },

        {
          key: "whatsapp",
          icon: <WhatsAppOutlined />,
          label: "WhatsApp",

          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();

            window.open(
              `https://wa.me/${resolveWhatsAppNumber(record.phone, record.country)}`
            );
          },
        },

        {
          key: "edit",
          icon: <EditOutlined />,
          label: "Edit Deal",

          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();

            console.log("edit");
          },
        },

        {
          key: "stage",
          icon: <SwapOutlined />,
          label: "Move Stage",

          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();

            console.log("move stage");
          },
        },
      ];

      // On mobile the three inline icons are folded into the same dropdown, so
      // the pinned column costs one icon of width instead of four.
      const mobileItems = [
        {
          key: "view",
          icon: <EyeOutlined />,
          label: "View Deal",
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            navigate(`/crm/deals/${record.deal_id}`);
          },
        },
        {
          key: "activity",
          icon: <MessageOutlined />,
          label: "Activity Logs",
          onClick: ({ domEvent }) => {
            domEvent.stopPropagation();
            onViewActivity?.(record);
          },
        },
        ...(record.contact_id
          ? [
              {
                key: "lead",
                icon: <UserOutlined />,
                label: "View Lead",
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  navigate(`/crm/leads/${record.contact_id}`);
                },
              },
            ]
          : []),
        ...menuItems,
      ];

      if (isMobile) {
        return (
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: mobileItems.map((m) => ({
                key: m.key,
                icon: m.icon,
                label: m.label,
                onClick: m.onClick,
              })),
            }}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      }

      return (
        <Space>

          {/* VIEW */}
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();

              navigate(
                `/crm/deals/${record.deal_id}`
              );
            }}
          />

          {/* ACTIVITY */}
          <Button
            type="text"
            icon={<MessageOutlined />}
            onClick={(e) => {
              e.stopPropagation();

              onViewActivity?.(record);
            }}
          />

          {/* LEAD */}
          <Button
            type="text"
            icon={<UserOutlined />}
            onClick={(e) => {
              e.stopPropagation();

              if (record.contact_id) {
                navigate(
                  `/crm/leads/${record.contact_id}`
                );
              }
            }}
          />

          {/* MORE */}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: menuItems.map((m) => ({
                key: m.key,
                icon: m.icon,
                label: m.label,
                onClick: m.onClick,
              })),
            }}
          >
            <Button
              type="text"
              icon={<MoreOutlined />}
              onClick={(e) =>
                e.stopPropagation()
              }
            />
          </Dropdown>

        </Space>
      );
  };

  // Actions stays pinned to the right at every size — the other columns scroll
  // horizontally underneath it. On mobile the icons collapse into a single
  // dropdown so the pinned column stays narrow enough to leave room to scroll.
  const actionColumn = [
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      align: "center",
      width: isMobile ? 56 : 240,
      render: (_, record) => renderActions(record),
    },
  ];

  const customColumns = customFields
  ?.filter(f => f.show_in_list)
  .map(f => ({
    title: f.field_name,
    render: (_, record) =>
      record.custom_fields?.[f.field_key] || "-"
  }));



  const columns = [...baseColumns, ...customColumns, ...actionColumn];

  return (
    <>
    <Table
      rowKey="deal_id"
      columns={columns}
      dataSource={deals}
      bordered
      onRow={(record) => ({
        onClick: () => navigate(`/crm/deals/${record.deal_id}`),
      })}
      rowClassName="clickable-row"
      size={isMobile ? "small" : "middle"}
      scroll={{ x: "max-content" }}
      />

    {/* Task Drawer */}
    <TaskDrawer
      open={taskDrawer}
      onClose={() => setTaskDrawer(false)}
      defaultDealId={selectedDeal?.deal_id}
      leads={leads}
      deals={deals}
      onSuccess={() => {
        setTaskDrawer(false);
      }}
    />  
    </>
  );
};

export default DealTable;